import { Box, Button, FormControl, TextField, Select, MenuItem } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from 'yup';
import colorConfigs from "../../../../configs/colorConfigs";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';


const BASE_FIELDS = {
    name: "",
    street:"",
    street2:"",
    city:"",
    state:"",
    zip:"",
    country:"",
}

const ADDITIONAL_FIELDS = {
    vat:"",
    businessid:"",
    email:"",
}

interface Country {
    odoo_id: number; // or string if it's a string
    name: string;
}

interface State {
    odoo_id: string; // or number
    name: string;
}

interface Props {
    title: string;
    currentStep: number;
    handleBack: () => void;
    handleNext: (data: any) => void;
    defaultData: any;
    iComponetTranslate: Array<{ label: string; value: string }>;
    customerData: any;
    countries: Country[];
    load_states: (countryId: string) => Promise<State[]>;
    update_state: () => void;
}

const CustomerDetailsForm = (props: Props) => {

    const { title, currentStep, handleBack, handleNext, defaultData, iComponetTranslate, customerData, countries, load_states, update_state} = props;
    const [initialData, setInitialData] = useState({...BASE_FIELDS,...ADDITIONAL_FIELDS})
    const [states, setStates] = useState<State[]>([]);
    const review = useRef(false);

    const t = (name: string) => {
        const ifExist: any = iComponetTranslate.filter((item: any) => item.label === name);
        if (ifExist.length > 0) {
            return ifExist[0].value ? ifExist[0].value : `No translate found (${ifExist[0].label})`;
        }
        return `No key found (${name})`;
    }
    useEffect(() => {
        if(defaultData){
            if (Object.keys(defaultData).length > 0) {
                setInitialData(defaultData)
            }
    }
    }, [defaultData])

    const AddressSchema = Yup.object().shape({
        
    });
    
    const CustomerSchema = Yup.object().shape({
        name: Yup.string().required('Required'),
        street: Yup.string().required('Required'),
        city: Yup.string().required('Required'),
        zip: Yup.number().required('Required'),
        country: Yup.string().required('Required'),
        email: Yup.string().email().required('Required'),
        vat: Yup.string()
        .required('VAT is required')
        .matches(
            /^[A-Za-z]{2}\d+$/,
            'VAT must start with two letters followed by numbers and be alphanumeric'
        ),
    });

    const formik = useFormik({
        initialValues:{
            name:initialData.name ,
            street: initialData.street ,
            street2: initialData.street2 ,
            city: initialData.city ,
            state: initialData.state ,
            zip: initialData.zip ,
            country: initialData.country ,
            email: initialData.email,
            vat:initialData.vat,
            businessid:initialData.businessid
        } ,
        enableReinitialize: true,
        validationSchema: currentStep !== 1 ? AddressSchema : CustomerSchema,
        onSubmit: (values) => {
            handleSendParentData(values);
        }
    }
    )

    const handleSendParentData = (formValues: any) => {
        switch (currentStep) {
            case 1:
                return handleNext({ "customer": formValues, "review": review.current });
            case 2:
                delete formValues.vat
                delete formValues.email
                delete formValues.businessid
                return handleNext({ "deliveryAddress": formValues, "review": review.current });
            case 3:
                delete formValues.vat
                delete formValues.email
                delete formValues.businessid
                return handleNext({ "invoiceAddress": formValues, "review": review.current });
            default:
                break;
        }
    }

    const handleReviewButtonClick = () => {
        review.current = true;
        formik.submitForm();
    };

    const handleCopy = () => {
        get_states(customerData.country)
        setInitialData(customerData)
    }

    const handleCountryChange = async(event:any) => {
        formik.handleChange(event);
        formik.setFieldValue('state', "")
        const selectedCountry = event.target.value as number;
        if(selectedCountry){
            get_states(selectedCountry)
        }
    };
    const get_states = async(selectedCountry:any) =>{
        let states_data = await load_states(selectedCountry);
        setStates(states_data)
    }

    return (
        <>
            <Box sx={{ width: "100%", padding: 1 }}>
                <Box sx={{display: "flex"}}>
                <Box className="fw-600" sx={{ color: "#012445", fontSize: "26px;", lineHeight: "normal", marginBottom: "40px", justifyContent: "space-between", display: "flex" }}>
                    {title && t(title)}
                </Box>
                {currentStep !== 1 && <Button
                            onClick={handleCopy}
                            variant="contained"
                            className="cm-btn-style"
                            sx={{
                                color: colorConfigs.btnColor.colorDarkBlue,
                                background: colorConfigs.btnStyle.color,
                                "&: hover": {
                                    color: colorConfigs.btnColor.colorDarkBlue,
                                    background: colorConfigs.btnStyle.color,
                                },
                                marginLeft: "10px",
                            }}
                        >
                            {t("CopyCustomerAddress")}
                        </Button>}
                        </Box>
                <form onSubmit={formik.handleSubmit}>

                    <Box sx={{ display: 'grid', gridTemplateRows: 'repeat(8, 1fr)', rowGap: 2 }} >
                        <Box sx={{ width: "100%" }}>
                            <div className="customer-form-lable">
                                {t("Name")}
                            </div>
                            <FormControl sx={{ width: "100%" }}>
                                <TextField
                                    className="cm-customer-filed"
                                    name="name"
                                    variant="outlined"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder={`${t("Name")}`}
                                />
                            </FormControl>
                            {formik.errors.name && formik.touched.name && <p className="cm-form-error">{formik.errors.name}</p>}
                        </Box>
                        <Box sx={{ width: "100%" }}>
                            <div className="customer-form-lable">
                                {t("Street")}
                            </div>
                            <FormControl sx={{ width: "100%" }}>
                                <TextField
                                    className="cm-customer-filed"
                                    name="street"
                                    variant="outlined"
                                    value={formik.values.street}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder={`${t("Street")}`}
                                />
                            </FormControl>
                            {formik.errors.street && formik.touched.street && <p className="cm-form-error">{formik.errors.street}</p>}
                        </Box>
                        <Box sx={{ width: "100%" }}>
                            <div className="customer-form-lable">
                                {t("street2")}
                            </div>
                            <FormControl sx={{ width: "100%" }}>
                                <TextField
                                    className="cm-customer-filed"
                                    name="street2"
                                    variant="outlined"
                                    value={formik.values.street2}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder={`${t("street2")}`}
                                />
                            </FormControl>
                            {formik.errors.street2 && formik.touched.street2 && <p className="cm-form-error">{formik.errors.street2}</p>}
                        </Box>
                       
                        <Box sx={{ flex: "60%", display: "flex", flexDirection: "column" }}>
                                <div className="customer-form-lable">
                                    {t("Country")}
                                </div>
                                <FormControl sx={{ width: "100%" }}>
                                    <Select
                                        className="cm-customer-filed"
                                        name="country"
                                        variant="outlined"
                                        value={formik.values.country}
                                        onChange={(e) => handleCountryChange(e)}
                                        onBlur={formik.handleBlur}
                                        disabled={!countries}
                                    >{countries && countries.map((item:any)=>{
                                        return(
                                            <MenuItem key={item.id} value={item.odoo_id}>{item.name}</MenuItem>
                                        )
                                    })}
                                    </Select>
                                </FormControl>
                                {formik.errors.country && formik.touched.country && <p className="cm-form-error">{formik.errors.country}</p>}
                            </Box>

                        <Box sx={{ width: "100%" }}>
                            <div className="customer-form-lable">
                                {t("State")}
                            </div>
                            <FormControl sx={{ width: "100%" }}>
                                <Select
                                    className="cm-customer-filed"
                                    name="state"
                                    variant="outlined"
                                    value={formik.values.state}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={!states}
                                >
                                    {states && states.map((item:any)=>{
                                        return(
                                            <MenuItem key={item.odoo_id} value={item.odoo_id}>{item.name}</MenuItem>
                                        )
                                    })}
                                    </Select>
                            </FormControl>
                            {formik.errors.state && formik.touched.state && <p className="cm-form-error">{formik.errors.state}</p>}
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between" }}  >
                            <Box sx={{ flex: "26%", display: "flex", flexDirection: "column" }}>
                                <div className="customer-form-lable">
                                    {t("Zip")}
                                </div>
                                <FormControl sx={{ width: "90%" }}>
                                    <TextField
                                        className="cm-customer-filed"
                                        name="zip"
                                        variant="outlined"
                                        value={formik.values.zip}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={`${t("Zip")}`}
                                    />
                                </FormControl>
                                {formik.errors.zip && formik.touched.zip && <p className="cm-form-error">{formik.errors.zip}</p>}
                            </Box >
                            <Box sx={{ width: "100%" }}>
                            <div className="customer-form-lable">
                                {t("City")}
                            </div>
                            <FormControl sx={{ width: "100%" }}>
                                <TextField
                                    className="cm-customer-filed"
                                    name="city"
                                    variant="outlined"
                                    value={formik.values.city}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder={`${t("City")}`}
                                />
                            </FormControl>
                            {formik.errors.city && formik.touched.city && <p className="cm-form-error">{formik.errors.city}</p>}
                        </Box>
                           
                        </Box>
                        {currentStep === 1 && 
                        (<>
                            <Box sx={{ width: "100%" }}>
                                <div className="customer-form-lable">
                                    {t("Email")}
                                </div>
                                <FormControl sx={{ width: "100%" }}>
                                    <TextField
                                        className="cm-customer-filed"
                                        name="email"
                                        variant="outlined"
                                        size="small"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={`${t("Email")}`}
                                    />
                                </FormControl>
                                {formik.errors.email && formik.touched.email && <p className="cm-form-error">{formik.errors.email}</p>}
                            </Box>
                            <Box sx={{ width: "100%" }}>
                                <div className="customer-form-lable">
                                    {t("businessid")}
                                </div>
                                <FormControl sx={{ width: "100%" }}>
                                    <TextField
                                        className="cm-customer-filed"
                                        name="businessid"
                                        variant="outlined"
                                        size="small"
                                        value={formik.values.businessid}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={`${t("businessid")}`}
                                    />
                                </FormControl>
                                {formik.errors.businessid && formik.touched.businessid && <p className="cm-form-error">{formik.errors.businessid}</p>}
                            </Box>
                            <Box sx={{ width: "100%" }}>
                                <div className="customer-form-lable">
                                    {t("vat")}
                                </div>
                                <FormControl sx={{ width: "100%" }}>
                                    <TextField
                                        className="cm-customer-filed"
                                        name="vat"
                                        variant="outlined"
                                        size="small"
                                        value={formik.values.vat}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={`${t("vat")}`}
                                    />
                                </FormControl>
                                {formik.errors.vat && formik.touched.vat && <p className="cm-form-error">{formik.errors.vat}</p>}
                            </Box>
                        </>)
                        }

                    </Box>

                    <Box display={"flex"} justifyContent={"space-between"}>
                        {currentStep !== 1 && <Button
                            onClick={handleBack}
                            variant="contained"
                            className="cm-btn-style"
                            sx={{
                                display: "flex",
                                justifyContent: "end",
                                color: colorConfigs.btnColor.colorDarkBlue,
                                background: colorConfigs.btnStyle.color,
                                "&: hover": {
                                    color: colorConfigs.btnColor.colorDarkBlue,
                                    background: colorConfigs.btnStyle.color,
                                },
                            }}
                        >
                            {t("Back")}
                        </Button>}
                        <Box display={"flex"}>
                            <Button
                                type="button"
                                variant="outlined"
                                endIcon={<ArrowForwardIcon />}
                                className="cm-btn-style"
                                sx={{
                                    mx:2,
                                    display: "flex",
                                    justifyContent: "end",
                                    color: colorConfigs.btnStyle.color,
                                    background: colorConfigs.btnColor.bgColorLightBlue,
                                    marginTop:2,
                                    "&: hover": {
                                        color: colorConfigs.btnStyle.color,
                                        background: colorConfigs.btnColor.colorDarkBlue,
                                    },
                                }}
                                onClick={handleReviewButtonClick}
                            >
                                {t("Review")}
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                endIcon={<ArrowForwardIcon />}
                                className="cm-btn-style"
                                sx={{
                                    display: "flex",
                                    justifyContent: "end",
                                    color: colorConfigs.btnStyle.color,
                                    background: colorConfigs.btnColor.bgColorLightBlue,
                                    marginTop:2,
                                    "&: hover": {
                                        color: colorConfigs.btnStyle.color,
                                        background: colorConfigs.btnColor.colorDarkBlue,
                                    },
                                }}
                            >
                                {t("Continue")}
                            </Button>
                        </Box>
                    </Box>

                </form>
            </Box>
        </>
    )



}  
  export default CustomerDetailsForm;